import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getApiUrl } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { barcodeSvg } from '../../utils/code128';
import { buildBarcodeValue } from '../../utils/barcodeFormat';
import {
  CLOTHING_SIZES,
  NO_SIZE_SENTINEL,
  formatSizeLabel,
} from '../../utils/productSize';

const adminHeaders = (token) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${token}`,
  'x-admin': token,
});

const productKey = (row) => {
  const product = row?.product;
  if (product && typeof product === 'object') {
    return String(product._id || product.id || row.productName || '');
  }
  return String(product || row?.productName || '');
};

const ProductFilterSelect = ({ value, options, onChange, fullWidth = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const selected = options.find((item) => item.id === value);
  const label = selected?.name || 'Select a product';
  const filtered = query.trim()
    ? options.filter((item) => item.name.toLowerCase().includes(query.trim().toLowerCase()))
    : options;

  useEffect(() => {
    const close = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const pick = (id) => {
    onChange(id);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={wrapRef} className={fullWidth ? 'w-full' : 'flex items-center gap-3'}>
      {fullWidth ? null : <span className="text-sm font-medium text-gray-500">Product</span>}
      <div className={fullWidth ? 'relative w-full' : 'relative'}>
        <button
          type="button"
          onClick={() => setOpen((on) => !on)}
          className={`${
            fullWidth ? 'flex w-full justify-between' : 'inline-flex'
          } items-center gap-2.5 h-11 rounded-lg border border-gray-200 bg-white px-3.5 text-sm font-medium text-gray-900 shadow-sm hover:border-gray-300 focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-500/20`}
        >
          <span className={`min-w-0 ${fullWidth ? 'truncate text-left' : 'whitespace-nowrap'}`}>
            {label}
          </span>
          <svg
            className={`shrink-0 text-gray-400 transition ${open ? 'rotate-180' : ''}`}
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden="true"
          >
            <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {open ? (
          <div className={`absolute left-0 z-20 mt-1 ${fullWidth ? 'w-full' : 'min-w-full w-max max-w-xs'} rounded-lg border border-gray-200 bg-white py-1 shadow-lg`}>
            <div className="px-2 pb-1">
              <input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search products"
                className="w-full rounded-md border px-2.5 py-1.5 text-sm"
                onKeyDown={(event) => {
                  if (event.key === 'Enter') event.preventDefault();
                }}
              />
            </div>
            <div className="max-h-64 overflow-y-auto">
              <button
                type="button"
                onClick={() => pick('')}
                className={`block w-full text-left px-3.5 py-2 text-sm ${
                  !value ? 'bg-pink-50 text-pink-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Select a product
              </button>
              {filtered.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => pick(item.id)}
                  className={`block w-full text-left px-3.5 py-2 text-sm ${fullWidth ? '' : 'whitespace-nowrap'} ${
                    value === item.id ? 'bg-pink-50 text-pink-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {item.name}
                </button>
              ))}
              {filtered.length === 0 ? (
                <p className="px-3.5 py-2 text-sm text-gray-400">No products match.</p>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const BarcodesAdmin = () => {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [barcodes, setBarcodes] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [wholesalePrice, setWholesalePrice] = useState('');
  const [retailPrice, setRetailPrice] = useState('');
  const [discountedPrice, setDiscountedPrice] = useState('');
  const [stock, setStock] = useState('1');
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [sizeMode, setSizeMode] = useState('size');
  const [sizeInput, setSizeInput] = useState('');
  const [colorInput, setColorInput] = useState('');
  const [colorsList, setColorsList] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [tab, setTab] = useState('add');
  const [filterProduct, setFilterProduct] = useState('');

  const selectedProduct = useMemo(
    () => products.find((item) => String(item._id) === selectedProductId) || null,
    [products, selectedProductId]
  );
  const productName = selectedProduct?.name || '';

  const withoutSize = sizeMode === 'none';
  const barcodeSizes = withoutSize ? [NO_SIZE_SENTINEL] : selectedSizes;
  const barcodeCount = (colorsList.length || 0) * (barcodeSizes.length || 0);

  const previews = useMemo(() => {
    if (!productName.trim() || wholesalePrice === '' || !barcodeSizes.length || !colorsList.length) return [];
    return colorsList.flatMap((color) =>
      barcodeSizes.map((size) => ({
        size,
        color,
        code: buildBarcodeValue({ productName, size, color, wholesalePrice }),
      }))
    );
  }, [productName, wholesalePrice, barcodeSizes, colorsList]);

  const productOptions = useMemo(() => {
    const map = new Map();
    products.forEach((product) => {
      const id = String(product._id || '');
      if (!id) return;
      map.set(id, { id, name: product.name || 'Product' });
    });
    barcodes.forEach((row) => {
      const id = productKey(row);
      if (!id || map.has(id)) return;
      map.set(id, { id, name: row.productName || 'Product' });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [barcodes, products]);

  const selectedProductBarcodes = useMemo(() => {
    if (!filterProduct) return [];
    return barcodes.filter((row) => productKey(row) === filterProduct);
  }, [barcodes, filterProduct]);

  const selectedProductName = productOptions.find((item) => item.id === filterProduct)?.name || '';

  const load = async () => {
    try {
      const [productRes, barcodeRes] = await Promise.all([
        fetch(getApiUrl('/api/admin/products'), { headers: { 'x-admin': 'true' } }),
        fetch(getApiUrl('/api/barcodes'), { headers: adminHeaders(token) }),
      ]);
      const productData = await productRes.json();
      const barcodeData = await barcodeRes.json();
      setProducts(productData.products || productData || []);
      if (!barcodeRes.ok) throw new Error(barcodeData.message || 'Could not load barcodes');
      setBarcodes(barcodeData.barcodes || []);
      setError('');
    } catch (err) {
      setError(err.message || 'Could not load barcodes');
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fillFromProduct = (id) => {
    setSelectedProductId(id);
    const product = products.find((item) => String(item._id) === id);
    if (!product) {
      setWholesalePrice('');
      setRetailPrice('');
      setDiscountedPrice('');
      return;
    }
    setWholesalePrice(product.wholesalePrice != null ? String(product.wholesalePrice) : '');
    const retail = product.retailPrice || product.oldPrice || product.price;
    setRetailPrice(retail != null && retail !== '' ? String(retail) : '');
    setDiscountedPrice(product.price != null ? String(product.price) : '');
  };

  const toggleSize = (size) => {
    setSelectedSizes((current) =>
      current.includes(size) ? current.filter((item) => item !== size) : [...current, size]
    );
  };

  const addSize = () => {
    const value = sizeInput.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '');
    if (!value) return setError('Type a size, then click Add');
    if (selectedSizes.includes(value)) {
      setSizeInput('');
      return;
    }
    setSelectedSizes((current) => [...current, value]);
    setSizeInput('');
    setError('');
  };

  const addColor = () => {
    const value = colorInput.trim();
    if (!value) return setError('Type one colour name, then click Add');
    if (colorsList.some((item) => item.toLowerCase() === value.toLowerCase())) {
      setColorInput('');
      return;
    }
    setColorsList((current) => [...current, value]);
    setColorInput('');
    setError('');
  };

  const removeColor = (value) => {
    setColorsList((current) => current.filter((item) => item !== value));
  };

  const create = async (event) => {
    event.preventDefault();
    if (!selectedProductId || !productName.trim()) return setError('Select a product');
    if (wholesalePrice === '') return setError('This product has no wholesale price. Edit the product first.');
    if (!withoutSize && !selectedSizes.length) return setError('Add at least one size');
    if (!colorsList.length) return setError('Add at least one colour');
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const res = await fetch(getApiUrl('/api/barcodes/bulk'), {
        method: 'POST',
        headers: adminHeaders(token),
        body: JSON.stringify({
          productId: selectedProductId,
          productName: productName.trim(),
          wholesalePrice: Number(wholesalePrice),
          retailPrice: retailPrice === '' ? Number(wholesalePrice) : Number(retailPrice),
          discountedPrice: discountedPrice === '' ? undefined : Number(discountedPrice),
          stock: Number(stock || 0),
          sizes: barcodeSizes,
          withoutSize,
          colors: colorsList,
          images: selectedProduct?.images || [],
          description: selectedProduct?.description || '',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Could not create barcodes');
      const createdCount = (data.barcodes || []).length;
      const skippedCount = (data.skipped || []).length;
      setNotice(
        `${createdCount} barcode${createdCount === 1 ? '' : 's'} created for ${productName}${skippedCount ? `, ${skippedCount} already existed` : ''}.`
      );
      setSelectedProductId('');
      setWholesalePrice('');
      setRetailPrice('');
      setDiscountedPrice('');
      setStock('1');
      setColorInput('');
      setColorsList([]);
      setSizeInput('');
      setSelectedSizes([]);
      setSizeMode('size');
      const createdId = String(
        data.product?._id || selectedProductId || data.barcodes?.[0]?.product || ''
      );
      if (createdId) setFilterProduct(createdId);
      await load();
      setTab('all');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const removeBarcode = async (row) => {
    const label = [formatSizeLabel(row.size), row.color].filter(Boolean).join(' · ');
    if (!window.confirm(`Delete barcode for ${row.productName}${label ? ` (${label})` : ''}?`)) return;
    try {
      const res = await fetch(getApiUrl(`/api/barcodes/${row._id}`), {
        method: 'DELETE',
        headers: adminHeaders(token),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Could not delete barcode');
      setBarcodes((current) => current.filter((item) => item._id !== row._id));
      setViewer((current) => {
        if (!current) return null;
        const rows = current.rows.filter((item) => item._id !== row._id);
        return rows.length ? { ...current, rows } : null;
      });
      setNotice('Barcode deleted.');
      setError('');
    } catch (err) {
      setError(err.message || 'Could not delete barcode');
    }
  };

  const removeProductBarcodes = async () => {
    if (!filterProduct) return;
    const count = selectedProductBarcodes.length;
    if (!count) return;
    if (!window.confirm(`Delete all ${count} barcode${count === 1 ? '' : 's'} for ${selectedProductName || 'this product'}?`)) {
      return;
    }
    try {
      const res = await fetch(getApiUrl(`/api/barcodes/product/${encodeURIComponent(filterProduct)}`), {
        method: 'DELETE',
        headers: adminHeaders(token),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Could not delete barcodes');
      setBarcodes((current) => current.filter((item) => productKey(item) !== filterProduct));
      setViewer(null);
      setNotice(`${data.deleted || count} barcode${(data.deleted || count) === 1 ? '' : 's'} deleted.`);
      setError('');
    } catch (err) {
      setError(err.message || 'Could not delete barcodes');
    }
  };

  const printRows = (rows, copies = 1) => {
    const labels = rows
      .flatMap((row) =>
        Array.from({ length: copies }, () => `
          <div class="label">
            ${barcodeSvg(row.code, 1, 64)}
            <div class="code">${row.code}</div>
          </div>`)
      )
      .join('');
    const popup = window.open('', '_blank', 'width=960,height=640');
    popup.document.write(`<!doctype html><html><head><title>theVastra.store barcodes</title>
      <style>
        body { font-family: Arial, sans-serif; text-align: center; background: #fff; margin: 0; padding: 16px; }
        .label { page-break-inside: avoid; padding: 16px 8px; }
        .label svg { display: block; margin: 0 auto; width: 100% !important; max-width: 100% !important; height: 64px !important; }
        .code { font-weight: 700; margin-top: 8px; letter-spacing: 0.4px; word-break: break-all; }
      </style></head><body>${labels}</body></html>`);
    popup.document.close();
    popup.focus();
    popup.print();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Barcodes</h1>
        <p className="text-sm text-gray-500 mt-1">
          {tab === 'add'
            ? 'Select a product to fill prices, then enter size and colour to generate barcodes.'
            : 'Choose a product to see every barcode for that item. View or print, then scan with theVastra.store app.'}
        </p>
      </div>

      <div className="flex bg-gray-100 rounded-xl p-1">
        <button
          type="button"
          onClick={() => setTab('add')}
          className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition ${
            tab === 'add' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
          }`}
        >
          Add barcode
        </button>
        <button
          type="button"
          onClick={() => setTab('all')}
          className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition ${
            tab === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
          }`}
        >
          All product barcodes
        </button>
      </div>

      {error ? <div className="rounded-lg bg-red-50 text-red-700 px-4 py-3 text-sm">{error}</div> : null}
      {notice ? <div className="rounded-lg bg-emerald-50 text-emerald-700 px-4 py-3 text-sm">{notice}</div> : null}

      {tab === 'add' ? (
      <form onSubmit={create} className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
        <ProductFilterSelect
          value={selectedProductId}
          options={products
            .map((product) => ({ id: String(product._id), name: product.name || 'Product' }))
            .sort((a, b) => a.name.localeCompare(b.name))}
          onChange={fillFromProduct}
        />
        <p className="text-xs text-gray-500 -mt-2">
          {selectedProduct
            ? `Prices filled from “${selectedProduct.name}”. Enter size and colour below.`
            : 'Choose a product. Wholesale, retail and discounted prices fill in automatically.'}
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          <label className="text-sm font-medium text-gray-700">
            Wholesale price
            <input
              type="number"
              min="0"
              value={wholesalePrice}
              readOnly
              className="mt-1 w-full rounded-lg border px-3 py-2 bg-gray-50 text-gray-700"
              placeholder="Select a product"
            />
            <span className="block mt-1 text-xs text-gray-500">
              Filled from the product. Encoded on the barcode as 111 + price + 000.
            </span>
          </label>
          <label className="text-sm font-medium text-gray-700">
            Retail price
            <input
              type="number"
              min="0"
              value={retailPrice}
              readOnly
              className="mt-1 w-full rounded-lg border px-3 py-2 bg-gray-50 text-gray-700"
              placeholder="Select a product"
            />
          </label>
          <label className="text-sm font-medium text-gray-700">
            Discounted price
            <input
              type="number"
              min="0"
              value={discountedPrice}
              readOnly
              className="mt-1 w-full rounded-lg border px-3 py-2 bg-gray-50 text-gray-700"
              placeholder="Select a product"
            />
          </label>
          <label className="text-sm font-medium text-gray-700">
            {withoutSize ? 'Stock' : 'Stock per size'}
            <input
              type="number"
              min="0"
              step="1"
              value={stock}
              onChange={(event) => {
                const value = event.target.value;
                if (value === '') return setStock('');
                setStock(String(Math.max(0, Math.floor(Number(value) || 0))));
              }}
              className="mt-1 w-full rounded-lg border px-3 py-2"
            />
          </label>
          <div className="text-sm font-medium text-gray-700 md:col-span-2">
            Colour
            <div className="mt-1 flex gap-2">
              <input
                value={colorInput}
                onChange={(event) => setColorInput(event.target.value)}
                placeholder="Type one colour, e.g. Maroon"
                className="flex-1 rounded-lg border px-3 py-2"
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addColor();
                  }
                }}
              />
              <button type="button" onClick={addColor} className="bg-[#56112A] text-white px-4 rounded-lg font-semibold">
                Add
              </button>
            </div>
            {colorsList.length ? (
              <div className="flex flex-wrap gap-2 mt-3">
                {colorsList.map((color) => (
                  <button
                    type="button"
                    key={color}
                    onClick={() => removeColor(color)}
                    className="h-9 px-3 rounded-lg bg-pink-600 text-white text-sm font-semibold"
                  >
                    {color} ×
                  </button>
                ))}
              </div>
            ) : null}
            <span className="block mt-1 text-xs text-gray-500">
              Type one colour and click Add. Repeat for more colours. Tap a colour to remove it.
            </span>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">
            Size option
            <select
              value={sizeMode}
              onChange={(event) => setSizeMode(event.target.value)}
              className="mt-1 w-full rounded-lg border px-3 py-2 bg-white"
            >
              <option value="size">Size</option>
              <option value="none">Without size</option>
            </select>
          </label>
          {withoutSize ? (
            <p className="text-xs text-gray-500 mt-2">
              No clothing size will be collected. One barcode is created for each colour.
            </p>
          ) : (
            <>
              <p className="text-sm font-medium text-gray-700 mt-4 mb-2">Size</p>
              <div className="flex gap-2">
                <input
                  value={sizeInput}
                  onChange={(event) => setSizeInput(event.target.value)}
                  placeholder="Type a size, e.g. M or 3XL"
                  className="flex-1 rounded-lg border px-3 py-2"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      addSize();
                    }
                  }}
                />
                <button type="button" onClick={addSize} className="bg-[#56112A] text-white px-4 rounded-lg font-semibold">
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {CLOTHING_SIZES.map((size) => {
                  const on = selectedSizes.includes(size);
                  return (
                    <button
                      type="button"
                      key={size}
                      onClick={() => toggleSize(size)}
                      className={`min-w-[44px] h-8 px-2 rounded-md border text-xs font-semibold ${
                        on ? 'bg-pink-600 text-white border-pink-600' : 'bg-white text-gray-700 border-gray-300'
                      }`}
                    >
                      {size}
                    </button>
                  );
                })}
                {selectedSizes
                  .filter((size) => !CLOTHING_SIZES.includes(size))
                  .map((size) => (
                    <button
                      type="button"
                      key={size}
                      onClick={() => toggleSize(size)}
                      className="min-w-[44px] h-8 px-2 rounded-md border text-xs font-semibold bg-pink-600 text-white border-pink-600"
                    >
                      {size} ×
                    </button>
                  ))}
              </div>
              <span className="block mt-1 text-xs text-gray-500">
                Type a size and click Add, or tap a size chip. One barcode is created for each colour × size.
              </span>
            </>
          )}
        </div>

        {previews.length ? (
          <div className="grid gap-3">
            {previews.map((item) => (
              <div key={`${item.color}-${item.size}`} className="border rounded-xl p-4 bg-white">
                <p className="text-xs font-semibold text-gray-500 mb-2">
                  {formatSizeLabel(item.size)}
                  {item.color ? ` · ${item.color}` : ''}
                </p>
                <BarcodePreview code={item.code} large />
              </div>
            ))}
          </div>
        ) : null}

        <button type="submit" disabled={saving || !selectedProductId} className="bg-pink-600 text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50">
          {saving ? 'Saving...' : `Generate ${barcodeCount} barcode${barcodeCount === 1 ? '' : 's'}`}
        </button>
      </form>
      ) : (
      <>
      <ProductFilterSelect
        value={filterProduct}
        options={productOptions}
        onChange={setFilterProduct}
      />

      {!filterProduct ? (
        <p className="text-sm text-gray-500">Select a product to see all of its barcodes.</p>
      ) : !selectedProductBarcodes.length ? (
        <p className="text-sm text-gray-500">No barcodes found for this product.</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden w-full">
          <div className="px-6 py-4 border-b flex items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-gray-900">{selectedProductName}</p>
              <p className="text-xs text-gray-500">
                {selectedProductBarcodes.length} barcode{selectedProductBarcodes.length === 1 ? '' : 's'}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <button
                type="button"
                className="text-gray-700 font-semibold text-sm"
                onClick={() => setViewer({ title: selectedProductName, rows: selectedProductBarcodes })}
              >
                View barcode
              </button>
              <button
                type="button"
                className="text-pink-600 font-semibold text-sm"
                onClick={() => printRows(selectedProductBarcodes)}
              >
                Print all
              </button>
              <button type="button" className="text-red-600 font-semibold text-sm" onClick={removeProductBarcodes}>
                Delete all
              </button>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {selectedProductBarcodes.map((row) => (
              <div key={row._id} className="p-6 sm:p-8">
                <BarcodePreview code={row.code} large />
                <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-sm text-gray-700 font-medium">
                    {formatSizeLabel(row.size)}
                    {row.color ? ` · ${row.color}` : ''}
                    {` · W ₹${row.wholesalePrice}`}
                    {` · R ₹${row.retailPrice}`}
                    {` · Stock ${row.stock}`}
                  </p>
                  <div className="whitespace-nowrap">
                    <button
                      type="button"
                      className="text-gray-800 font-semibold mr-4"
                      onClick={() => setViewer({ title: row.productName, rows: [row] })}
                    >
                      View barcode
                    </button>
                    <button type="button" className="text-pink-600 font-semibold mr-4" onClick={() => printRows([row])}>
                      Print
                    </button>
                    <button type="button" className="text-red-600 font-semibold" onClick={() => removeBarcode(row)}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </>
      )}

      {viewer ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-y-auto p-6 sm:p-8">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">View barcode</h3>
                <p className="text-sm text-gray-500 mt-1">{viewer.title}</p>
              </div>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-700 text-2xl font-bold leading-none"
                onClick={() => setViewer(null)}
                aria-label="Close barcode view"
              >
                ×
              </button>
            </div>
            <div className="space-y-6">
              {viewer.rows.map((row) => (
                <div key={row._id || row.code} className="border rounded-xl p-5 bg-white">
                  <div className="w-full bg-white" style={{ minHeight: 140 }}>
                    <BarcodePreview code={row.code} large />
                  </div>
                  <p className="text-center text-sm text-gray-600 mt-3">
                    {row.productName} · {formatSizeLabel(row.size)}
                    {row.color ? ` · ${row.color}` : ''}
                    {row.wholesalePrice != null ? ` · W ₹${row.wholesalePrice}` : ''}
                  </p>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <button
                type="button"
                className="flex-1 py-3 rounded-xl bg-pink-600 text-white font-semibold"
                onClick={() => printRows(viewer.rows)}
              >
                Print
              </button>
              <button
                type="button"
                className="flex-1 py-3 rounded-xl border border-gray-200 font-semibold"
                onClick={() => setViewer(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const BarcodePreview = ({ code, large = false }) => (
  <div className="flex flex-col items-center bg-white w-full">
    <div
      className="w-full bg-white px-2 py-4 [&_svg]:!block [&_svg]:!w-full [&_svg]:!max-w-full"
      dangerouslySetInnerHTML={{ __html: barcodeSvg(code, 1, large ? 140 : 88) }}
    />
    <p className="mt-3 font-mono text-sm font-semibold break-all text-center">{code}</p>
  </div>
);

export default BarcodesAdmin;
